import { ComponentPropsWithoutRef, forwardRef, useContext } from "react";
import { LayoutUtilProps } from "../../../types";
import { Flex } from "../../../components/Flex";
import { ToolbarContext } from "./internal/ToolbarContext";

import styles from "./ToolbarControlGroup.module.scss";

/**
 * Props for the ToolbarControlGroup component
 *
 * @extends ComponentPropsWithoutRef<'div'>
 * @extends LayoutUtilProps
 */
export type ToolbarControlGroupProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * Accessible label for the control group
     */
    "aria-label"?: string;
  };

/**
 * ToolbarControlGroup component provides a flex container for grouping related toolbar controls.
 * Can be used for filters, buttons, or other toolbar items.
 *
 * Features:
 * - Groups related toolbar controls in a flex container
 * - Supports accessibility with aria-label
 * - Handles overflow and wrapping based on toolbar context
 * - Integrates with layout utilities for spacing and alignment
 *
 * @example
 * <ToolbarControlGroup aria-label="Formatting controls">
 *   <Toolbar.Button icon={BoldIcon} aria-label="Bold" />
 *   <Toolbar.Button icon={ItalicIcon} aria-label="Italic" />
 * </ToolbarControlGroup>
 */
const ToolbarControlGroupElement = forwardRef<
  HTMLDivElement,
  ToolbarControlGroupProps
>((props, ref) => {
  const { children, "aria-label": ariaLabel, ...rest } = props;
  const { overflow } = useContext(ToolbarContext);

  return (
    <Flex
      ref={ref}
      alignItems="center"
      gap={1}
      wrap={overflow === "collapse" ? "nowrap" : "wrap"}
      data-anv="toolbar-control-group"
      role="group"
      aria-label={ariaLabel ? ariaLabel : "Toolbar controls"}
      className={styles["toolbar-control-group"]}
      {...rest}
    >
      {children}
    </Flex>
  );
});

ToolbarControlGroupElement.displayName = "ToolbarControlGroup";

export const ToolbarControlGroup = ToolbarControlGroupElement;
